import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Download, Check, Mail, ArrowRight, ShieldCheck, Clock4, Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import Reveal from "@/components/site/Reveal";
import { trackLeadConversion } from "@/lib/analytics";
import { metaLead } from "@/lib/metaPixel";
import { getAttribution } from "@/lib/attribution";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAGNET_ID = "angehoerigen-beschwerde";
const PREVIEW = "/angehoerigen-mini-preview.webp";

const TRUST_POINTS = [
  {
    icon: Clock4,
    title: "In 5 Minuten antworten",
    desc: "Schluss mit stundenlangem Grübeln über die richtige Formulierung.",
  },
  {
    icon: ShieldCheck,
    title: "Rechtssicher & deeskalierend",
    desc: "Aus echter Praxis – getestet in über 40 Pflegeeinrichtungen.",
  },
  {
    icon: Sparkles,
    title: "Sofort als PDF im Postfach",
    desc: "Kein Newsletter-Spam. Ein Dokument. Direkt einsatzbereit.",
  },
];

const ROLES = [
  ["pflegekraft", "Pflegekraft"],
  ["pdl",         "PDL / Leitung"],
  ["leitung",     "Heim-/GF"],
  ["andere",      "Andere"],
];

export default function AngehoerigenLeitfaden() {
  const [form, setForm] = useState({ name: "", email: "", role: "pflegekraft", phone: "", bundesland: "", company: "", position: "" });
  const [state, setState] = useState({ loading: false, success: false, error: null });

  useEffect(() => {
    document.title =
      "Angehörigen-Beschwerde in 5 Min beantworten · Kostenloser Leitfaden | Dickies-Campus";
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
    m.content =
      "Kostenloser 1-Pager mit fertiger Antwort-Mail für die häufigste Angehörigen-Beschwerde. Deeskalierend, rechtssicher, sofort einsatzbereit – direkt aus dem Dickies-Campus.";
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setState({ loading: true, success: false, error: null });
    try {
      const res = await axios.post(`${API}/leadmagnet/${MAGNET_ID}`, {
        email: form.email,
        name: form.name,
        role: form.role,
        phone: form.phone,
        bundesland: form.bundesland,
        company: form.company,
        position: form.position,
        consent: true,
        attribution: getAttribution(),
      });
      // DOI: kein direkter Download mehr — User muss E-Mail bestätigen
      void res; // response just used to confirm pending_confirmation
      trackLeadConversion(5, "leadmagnet:angehoerigen-beschwerde");
      metaLead("leadmagnet:angehoerigen-beschwerde");
      setState({ loading: false, success: true, error: null });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const msg = (status === 400 && typeof detail === "string")
        ? detail
        : status === 422
          ? "Bitte gültige E-Mail eingeben."
          : "Bitte später erneut versuchen.";
      setState({ loading: false, success: false, error: msg });
    }
  };

  return (
    <div className="min-h-screen bg-cream-50">
      <Header />

      {/* ============================== HERO ============================== */}
      <section className="relative pt-28 md:pt-36 pb-20 md:pb-28 bg-peach-50 overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-32 top-20 w-[460px] h-[460px] rounded-full opacity-60"
          style={{
            background: "radial-gradient(circle, rgba(184,80,66,0.18) 0%, transparent 70%)",
          }}
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center">
          {/* LEFT - copy */}
          <Reveal>
            <div>
              <div className="flex items-center gap-3 mb-6">
                <span className="h-px w-10 bg-terra" />
                <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-terra"
                      data-testid="leadmagnet-kicker">
                  Kostenlos · PDF · Sofort-Download
                </span>
              </div>

              <h1 className="font-display text-[2.4rem] sm:text-5xl lg:text-[3.4rem] text-sand-800 font-normal tracking-tight leading-[1.05]"
                  data-testid="leadmagnet-h1">
                Aggressive Angehörigen-Mail im Postfach? <br />
                <span className="text-terra italic">In 5 Minuten professionell antworten.</span>
              </h1>

              <p className="mt-7 text-sand-700 text-[16px] sm:text-[17px] leading-[1.65] max-w-xl">
                Der 1-Pager-Auszug aus unserem <b>Dickies-Campus</b>: das
                4-Schritte-Prinzip jeder Beschwerde-Antwort und eine fertige
                Antwort-Mail für den häufigsten Fall – „Meine Mutter lag
                ungepflegt im Bett". Drei Wörter anpassen, abschicken, fertig.
              </p>

              <ul className="mt-8 space-y-3.5 text-[15px] text-sand-700">
                {TRUST_POINTS.map((p) => (
                  <li key={p.title} className="flex items-start gap-3">
                    <span className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-full bg-terra/10 text-terra shrink-0">
                      <p.icon className="h-4 w-4" strokeWidth={2} />
                    </span>
                    <span>
                      <span className="font-semibold text-sand-800">{p.title}.</span>{" "}
                      <span className="text-sand-600">{p.desc}</span>
                    </span>
                  </li>
                ))}
              </ul>

              <div className="mt-9 flex items-center gap-3 text-[12.5px] text-sand-500">
                <span className="inline-flex h-2 w-2 rounded-full bg-brand-rose animate-pulse" />
                Bereits <b className="text-sand-700 mx-1">1.240+</b> Pflegekräfte haben den Leitfaden abgerufen.
              </div>
            </div>
          </Reveal>

          {/* RIGHT - form card */}
          <Reveal delay={120}>
            <div className="relative">
              {/* Preview thumbnail */}
              <div className="absolute -top-10 -left-10 hidden lg:block w-44 z-10">
                <div className="rounded-2xl overflow-hidden border border-cream-300 shadow-soft rotate-[-6deg] bg-white">
                  <img
                    src={PREVIEW}
                    alt="Vorschau Mini-Leitfaden"
                    className="w-full h-auto block"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                </div>
              </div>

              <div className="rounded-3xl bg-white border border-cream-300 shadow-hover p-7 md:p-9"
                   data-testid="leadmagnet-form-card">
                {!state.success ? (
                  <>
                    <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-clay-dark mb-3">
                      Mini-Leitfaden anfordern
                    </div>
                    <h2 className="font-display text-2xl md:text-[1.7rem] text-sand-800 leading-tight">
                      Trag deine E-Mail ein – PDF kommt sofort.
                    </h2>

                    <form onSubmit={submit} className="mt-7 grid gap-4">
                      <div>
                        <label className="text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-2 block">
                          Du bist …
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                          {ROLES.map(([v, l]) => (
                            <button
                              key={v} type="button"
                              onClick={() => setForm({ ...form, role: v })}
                              data-testid={`leadmagnet-role-${v}`}
                              className={`rounded-full px-3 h-10 text-[12.5px] font-semibold border transition-all ${
                                form.role === v
                                  ? "border-terra bg-terra text-white"
                                  : "border-cream-300 text-sand-700 hover:border-clay bg-cream-50"
                              }`}
                            >{l}</button>
                          ))}
                        </div>
                      </div>

                      <input
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="Vor- und Nachname *"
                        required minLength={2}
                        data-testid="leadmagnet-name"
                        className="rounded-2xl border border-cream-300 bg-cream-50 px-5 h-12 text-[15px] outline-none focus:border-terra"
                      />
                      <input
                        required type="email" value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        placeholder="E-Mail-Adresse *"
                        data-testid="leadmagnet-email"
                        className="rounded-2xl border border-cream-300 bg-cream-50 px-5 h-12 text-[15px] outline-none focus:border-terra"
                      />
                      <input
                        required type="tel" inputMode="tel"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        placeholder="Telefon (z. B. 0211 12345678) *"
                        data-testid="leadmagnet-phone"
                        className="rounded-2xl border border-cream-300 bg-cream-50 px-5 h-12 text-[15px] outline-none focus:border-terra"
                      />
                      <select
                        required value={form.bundesland}
                        onChange={(e) => setForm({ ...form, bundesland: e.target.value })}
                        data-testid="leadmagnet-bundesland"
                        className="rounded-2xl border border-cream-300 bg-cream-50 px-5 h-12 text-[15px] outline-none focus:border-terra"
                      >
                        <option value="">Bundesland * (bitte wählen)</option>
                        <option value="BW">Baden-Württemberg</option>
                        <option value="BY">Bayern</option>
                        <option value="BE">Berlin</option>
                        <option value="BB">Brandenburg</option>
                        <option value="HB">Bremen</option>
                        <option value="HH">Hamburg</option>
                        <option value="HE">Hessen</option>
                        <option value="MV">Mecklenburg-Vorpommern</option>
                        <option value="NI">Niedersachsen</option>
                        <option value="NW">Nordrhein-Westfalen</option>
                        <option value="RP">Rheinland-Pfalz</option>
                        <option value="SL">Saarland</option>
                        <option value="SN">Sachsen</option>
                        <option value="ST">Sachsen-Anhalt</option>
                        <option value="SH">Schleswig-Holstein</option>
                        <option value="TH">Thüringen</option>
                        <option value="AT">Österreich</option>
                        <option value="CH">Schweiz</option>
                        <option value="OTHER">Anderes</option>
                      </select>
                      {(form.role === "pdl" || form.role === "leitung") && (
                        <>
                          <input
                            required value={form.company}
                            onChange={(e) => setForm({ ...form, company: e.target.value })}
                            placeholder="Pflegedienst / Einrichtung *"
                            data-testid="leadmagnet-company"
                            className="rounded-2xl border border-cream-300 bg-cream-50 px-5 h-12 text-[15px] outline-none focus:border-terra"
                          />
                          <input
                            required value={form.position}
                            onChange={(e) => setForm({ ...form, position: e.target.value })}
                            placeholder="Position (z. B. PDL, WBL) *"
                            data-testid="leadmagnet-position"
                            className="rounded-2xl border border-cream-300 bg-cream-50 px-5 h-12 text-[15px] outline-none focus:border-terra"
                          />
                        </>
                      )}

                      <Button
                        type="submit"
                        disabled={state.loading}
                        data-testid="leadmagnet-submit"
                        className="rounded-full bg-terra hover:bg-terra-dark text-white h-12 px-6 text-[15px] font-semibold mt-1"
                      >
                        {state.loading ? "Wird gesendet …" : (
                          <span className="inline-flex items-center gap-2">
                            <Download className="h-4 w-4" strokeWidth={1.8} />
                            Leitfaden per E-Mail anfordern
                          </span>
                        )}
                      </Button>

                      {state.error && (
                        <p className="text-[13px] text-terra font-medium" data-testid="leadmagnet-error">
                          {state.error}
                        </p>
                      )}
                      <p className="text-[11px] text-sand-500 leading-snug">
                        Kein Newsletter-Spam. Mit dem Absenden stimmst du der Verarbeitung
                        deiner Daten gemäß{" "}
                        <Link to="/datenschutz" className="underline">Datenschutzerklärung</Link> zu.
                        Wir senden dir eine Bestätigungs-Mail — erst nach Klick bekommst du den Leitfaden.
                      </p>
                    </form>
                  </>
                ) : (
                  <div data-testid="leadmagnet-success" className="text-center py-4">
                    <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-brand-rose/30 text-brand-darkred mx-auto">
                      <Check className="h-7 w-7" strokeWidth={2} />
                    </div>
                    <h3 className="mt-5 font-display text-2xl text-sand-800">Fast geschafft!</h3>
                    <p className="mt-3 text-sand-600 text-[14px] leading-relaxed">
                      Wir haben dir gerade eine E-Mail an <strong className="text-sand-800">{form.email}</strong> geschickt.
                      Bitte klicke darin auf den Bestätigungs-Link — danach bekommst du den Leitfaden sofort.
                    </p>
                    <div className="mt-7 rounded-2xl bg-peach-50 border border-cream-300 p-5 text-left">
                      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-terra mb-2">
                        Bonus für dich
                      </div>
                      <p className="text-[14px] text-sand-700 leading-relaxed">
                        Im <b>Dickies-Campus</b> bekommst du <b>4 weitere</b> fertige
                        Mail-Templates, ein 4-stufiges Eskalations-Konzept und über
                        100 Mustervorlagen. <br />
                        <Link to="/campus"
                              data-testid="leadmagnet-campus-cta"
                              className="mt-3 inline-flex items-center gap-1.5 text-terra font-semibold underline">
                          Dickies-Campus ansehen <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============================== SOCIAL PROOF / BRIDGE ============================== */}
      <section className="bg-cream-50 py-20 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <blockquote className="font-display italic text-2xl md:text-[1.85rem] text-sand-800 leading-[1.4] text-center">
              „Ich hab den Leitfaden ausgedruckt und neben den PC gehängt.<br />
              Seitdem brauche ich für jede Angehörigen-Mail keine 5&nbsp;Minuten mehr.
              <span className="text-terra"> Das war 2025 die beste Entscheidung.</span>"
            </blockquote>
            <p className="mt-7 text-center text-sand-600 text-[13px] uppercase tracking-[0.22em] font-semibold">
              Tanja R. · Pflegedienstleitung · Düsseldorf
            </p>
          </Reveal>
        </div>
      </section>

      {/* ============================== CAMPUS BRIDGE CTA ============================== */}
      <section className="bg-peach-50 py-28 md:py-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-12 items-center">
              <div>
                <div className="flex items-center gap-3 mb-5">
                  <span className="h-px w-10 bg-terra" />
                  <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-terra">
                    Das ist erst der Anfang
                  </span>
                </div>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-[2.6rem] text-sand-800 font-normal tracking-tight leading-[1.1]">
                  Im Dickies-Campus gibt's noch <span className="text-terra">vier weitere</span> fertige Mail-Templates.
                </h2>
                <p className="mt-6 text-sand-700 text-[15px] leading-[1.7] max-w-xl">
                  Plus MDK-Soforthilfe-Protokoll, SIS-Entbürokratisierungs-Checkliste,
                  Erschöpfungs-Dienstplan und über 100 Mustervorlagen – wachsende
                  Bibliothek, monatlich erweitert. <b>Inkludiert in jeder Weiterbildung
                  bei Dickies-Campus.</b>
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link to="/campus" data-testid="leadmagnet-bridge-campus">
                    <Button className="rounded-full bg-terra hover:bg-terra-dark text-white h-12 px-7 text-[15px] font-semibold">
                      <span className="inline-flex items-center gap-2">
                        Dickies-Campus ansehen <ArrowRight className="h-4 w-4" />
                      </span>
                    </Button>
                  </Link>
                  <Link to="/pflegekraefte" data-testid="leadmagnet-bridge-kurse">
                    <Button variant="outline"
                            className="rounded-full border-clay text-sand-800 hover:bg-cream-100 h-12 px-7 text-[15px] font-semibold">
                      Weiterbildungen entdecken
                    </Button>
                  </Link>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {[
                  ["Mail-Templates",      "5 Fälle"],
                  ["Mustervorlagen",      "100 +"],
                  ["Live-Briefings",      "Monatlich"],
                  ["MDK-Soforthilfe",     "24/7"],
                ].map(([k, v]) => (
                  <div key={k} className="rounded-3xl bg-white border border-cream-300 p-6 shadow-soft">
                    <div className="font-display text-[2rem] text-terra leading-none">{v}</div>
                    <div className="mt-2 text-[13px] uppercase tracking-[0.2em] font-bold text-clay-dark">
                      {k}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
